import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { AcademyEntitySet } from '../entities/academy-entities.model';
import { fromWireEnums, toWireEnums } from '../entities/academy-enums.model';

/**
 * Mutations and by-id reads for the academy entity sets, served by the per-entity
 * `GenericController<TEntity, TViewModel>` mounted at the plain root `/{Set}` (e.g.
 * `TranscriptCuesController`, `ChaptersController`, `CoursesController`) — base
 * `apiUrl.academy_service` (NO `/odata`). It exposes `POST` (create), `GET ?id=`
 * (by id), `PATCH` (update) and `DELETE ?id=` (remove); the entity key always rides
 * as a **query-string** `id`, never OData `/{Set}({id})` routing.
 *
 * The read-only OData list (`GET /odata/{Set}`) lives in the sibling
 * `AcademyODataService` — the two surfaces are deliberately separate controllers on
 * the backend, so a `POST` must never target the `/odata` base (it is GET-only).
 */
@Injectable({ providedIn: 'root' })
export class AcademyCrudService {
  private readonly http = inject(HttpClient);
  /** `GenericController` CRUD root (`/{Set}`) — plain `academy_service`, no `/odata`. */
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;

  /**
   * `POST /{Set}` — create (body = `*CreateRequest` model). Status 200/201 agnostic.
   * Enum fields are translated to their numeric wire codes: the deployed API rejects
   * enum NAMES on write bodies (`toWireEnums`). The echoed entity comes back with
   * numeric codes and is normalized to names (`fromWireEnums`).
   */
  public create<T>(set: AcademyEntitySet, viewModel: unknown): Observable<T> {
    return this.http
      .post<T>(`${this.baseUrl}/${set}`, toWireEnums(viewModel), {
        withCredentials: true,
      })
      .pipe(map((created) => fromWireEnums(created)));
  }

  /**
   * `GET /{Set}?id={guid}` — read ONE entity by key (`GetByIdAsync(Guid id)`), keyed
   * by a **query-string** `id`. The single read carries fields the list/discover
   * projections omit — notably `CoverUrl` on `Courses`/`LearningPaths`. Enum fields
   * arrive as numeric wire codes and are translated to names (`fromWireEnums`).
   * Tolerates a bare object, a `value` envelope, or a one-item array; resolves to
   * `null` when nothing matches.
   */
  public getById<T>(set: AcademyEntitySet, id: string): Observable<T | null> {
    return this.http
      .get<unknown>(`${this.baseUrl}/${set}`, {
        params: new HttpParams().set('id', id),
        withCredentials: true,
      })
      .pipe(map((res) => this.toSingle<T>(res)));
  }

  /**
   * `PATCH /{Set}?id={guid}` — update one entity (`PatchAsync`), keyed by a
   * **query-string** `id`. Enum fields are translated to their numeric wire codes on
   * write bodies (`toWireEnums`). The echoed entity comes back with numeric codes and
   * is normalized to names (`fromWireEnums`).
   */
  public update<T>(
    set: AcademyEntitySet,
    id: string,
    viewModel: unknown
  ): Observable<T> {
    const idKey = this.getEntityIdKey(set);
    const body = {
      ...(typeof viewModel === 'object' && viewModel !== null ? viewModel : {}),
      [idKey]: id,
    };
    return this.http
      .patch<T>(`${this.baseUrl}/${set}`, toWireEnums(body), {
        params: new HttpParams().set('id', id),
        withCredentials: true,
      })
      .pipe(map((updated) => fromWireEnums(updated)));
  }

  private getEntityIdKey(set: AcademyEntitySet): string {
    switch (set) {
      case 'Courses':
        return 'CourseId';
      case 'LiveEvents':
        return 'LiveEventId';
      case 'LearningPaths':
        return 'LearningPathId';
      case 'Chapters':
        return 'ChapterId';
      case 'CourseItems':
        return 'CourseItemId';
      case 'LiveAttendances':
        return 'LiveAttendanceId';
      case 'LiveQuestions':
        return 'LiveQuestionId';
      case 'LiveRatings':
        return 'LiveRatingId';
      case 'LiveSubscriptions':
        return 'LiveSubscriptionId';
      case 'ChatMessages':
        return 'ChatMessageId';
      case 'TenantBrandings':
        return 'TenantBrandingId';
      case 'TranscriptCues':
        return 'TranscriptCueId';
      case 'UsageMetrics':
        return 'UsageMetricId';
      case 'Certificates':
        return 'CertificateId';
      default: {
        const s = set as string;
        return s.endsWith('s') ? `${s.slice(0, -1)}Id` : `${s}Id`;
      }
    }
  }

  /**
   * `DELETE /{Set}?id={guid}` — remove one entity (`RemoveAsync(Guid id)`), keyed by
   * a **query-string** `id` (api-tester §"📝 Capítulos e Transcrição"). No body, no
   * enum translation.
   */
  public delete(set: AcademyEntitySet, id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${set}`, {
      params: new HttpParams().set('id', id),
      withCredentials: true,
    });
  }

  /** Resolve a single-entity read (bare object / `value` envelope / one-item array) to one entity. */
  private toSingle<T>(res: unknown): T | null {
    if (res == null) return null;
    if (Array.isArray(res)) return (fromWireEnums(res)[0] as T) ?? null;
    const env = res as { value?: unknown[] };
    if (Array.isArray(env.value))
      return (fromWireEnums(env.value)[0] as T) ?? null;
    return fromWireEnums(res as T);
  }
}
